/**
 * WebdeskSyncButton — pulsante quick per lanciare una sync manuale con Webdesk.
 * Mostra badge di stato (ultima sync). Se integrazione non abilitata, si nasconde.
 */
import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Cloud, RefreshCw, CheckCircle2, XCircle, Clock } from "lucide-react";
import api from "@/api";

export default function WebdeskSyncButton() {
  const qc = useQueryClient();
  const { data: st } = useQuery({
    queryKey: ["webdesk-status"],
    queryFn: () => api.get("/webdesk/status").then((r) => r.data),
    refetchInterval: 15_000,
  });

  const sync = useMutation({
    mutationFn: () => api.post("/webdesk/sync").then((r) => r.data),
    onSuccess: (d) => {
      const s = d.stats || {};
      toast.success(`Webdesk ${d.status} — importate ${s.imported || 0}, aggiornate ${s.updated || 0}`);
      qc.invalidateQueries({ queryKey: ["webdesk-status"] });
      qc.invalidateQueries({ queryKey: ["fatture"] });
      qc.invalidateQueries({ queryKey: ["previsionale"] });
      qc.invalidateQueries({ queryKey: ["cash-flow"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Sync Webdesk fallita"),
  });

  if (!st?.enabled) return null;

  const lastIso = st.last_sync_at || "";
  const lastLabel = lastIso ? lastIso.slice(0, 16).replace("T", " ") : "mai";
  const statusIcon = st.last_sync_status === "success" ? <CheckCircle2 className="w-3 h-3 text-emerald-600" /> :
                     st.last_sync_status === "failed" ? <XCircle className="w-3 h-3 text-rose-600" /> :
                     <Clock className="w-3 h-3 text-slate-400" />;

  return (
    <div className="inline-flex flex-col items-end gap-1" data-testid="webdesk-quick-sync">
      <button
        onClick={() => sync.mutate()}
        disabled={sync.isPending}
        className="inline-flex items-center gap-2 px-3 py-2 bg-slate-800 hover:bg-slate-900 text-white text-xs font-semibold rounded-sm disabled:opacity-60"
      >
        <Cloud className="w-4 h-4" />
        <RefreshCw className={`w-3.5 h-3.5 ${sync.isPending ? "animate-spin" : ""}`} />
        {sync.isPending ? "Sync Webdesk…" : "Sincronizza Webdesk"}
      </button>
      <div className="text-[10px] text-slate-500 flex items-center gap-1">
        {statusIcon}<span>Ultima: {lastLabel}</span>
      </div>
    </div>
  );
}
